import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError, CreateAxiosDefaults } from 'axios';
 import { useAuthStore } from '@/store/auth.store'; // Direct import
 
// --- MOCK API CONFIGURATION ---
 const MOCK_API = false; // Set to true to enable mock API, false for real API
 const MOCK_DELAY = 500; // Simulate network delay
 
interface User {
   id: string;
   email: string;
   firstName?: string;
   lastName?: string;
   phoneNumber?: string;
   role?: { name: string };
 }
 
interface ApiErrorResponse {
   message: string;
 }
 
const API_BASE_URL: string = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080/api';
 
const axiosConfig: CreateAxiosDefaults = {
  baseURL: API_BASE_URL,
  withCredentials: true,
};

const realApiService: AxiosInstance = axios.create(axiosConfig);
 
// Request interceptor — auth cookie is sent automatically via withCredentials
realApiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    return config;
  },
   (error: AxiosError): Promise<AxiosError> => {
     return Promise.reject(error);
   }
 );
 
// Response interceptor for real API
 realApiService.interceptors.response.use(
   (response: AxiosResponse): AxiosResponse => {
     return response;
   },
   (error: AxiosError<ApiErrorResponse>): Promise<AxiosError> => {
     const authStore = useAuthStore();
     const requestUrl = error.config?.url ?? '';
     const isAuthSessionCheck =
       requestUrl.includes('/auth/me') ||
       requestUrl.includes('/auth/logout') ||
       requestUrl.includes('/auth/login');

     if (error.response?.status === 401 && !isAuthSessionCheck) {
       authStore.clearSession(false);
     }
     return Promise.reject(error);
   }
 );
 
// Mock API Service
 const mockApiService = {
   post: <T = any, R = AxiosResponse<T>>(url: string, data?: any): Promise<R> => {
     return new Promise((resolve, reject) => {
       setTimeout(() => {
         if (url.includes('/auth/login')) {
           const { email, password } = data;
           if (email === 'owner@example.com' && password === 'password') {
             const mockUser: User = { id: 'owner1', email: 'owner@example.com', firstName: 'Salon', lastName: 'Owner', role: { name: 'Owner' } };
             resolve({ data: { data: { user: mockUser, token: 'mock-owner-token' } } } as R);
           } else if (email === 'emp@example.com' && password === 'password') {
             const mockUser: User = { id: 'emp1', email: 'emp@example.com', firstName: 'Salon', lastName: 'Employee', role: { name: 'Employee' } };
             resolve({ data: { data: { user: mockUser, token: 'mock-employee-token' } } } as R);
           } else if (email === 'user@example.com' && password === 'password') {
             const mockUser: User = { id: 'user1', email: 'user@example.com', firstName: 'Test', lastName: 'User', role: { name: 'Client' } };
             resolve({ data: { data: { user: mockUser, token: 'mock-user-token' } } } as R);
           } else {
             reject({ response: { data: { message: 'Invalid credentials' }, status: 401 }, isAxiosError: true, toJSON: () => ({}) });
           }
         } else if (url.includes('/auth/register')) {
           const { email } = data;
           if (email === 'exists@example.com') {
              reject({ response: { data: { message: 'Email already exists' }, status: 400 }, isAxiosError: true, toJSON: () => ({}) });
           } else {
             // Simulate successful registration (backend would typically return the new user or a success message)
             resolve({ data: { message: 'Registration successful' } } as R);
           }
         } else if (url.includes('/gallery/upload')) {
           resolve({ data: { message: 'Image uploaded successfully, pending approval.' } } as R);
         } else {
           reject({ response: { data: { message: `Mock endpoint for POST ${url} not found` }, status: 404 }, isAxiosError: true, toJSON: () => ({}) });
         }
       }, MOCK_DELAY);
     });
   },
   get: <T = any, R = AxiosResponse<T>>(url: string, config?: InternalAxiosRequestConfig): Promise<R> => {
     return new Promise((resolve, reject) => {
       setTimeout(() => {
         if (url.includes('/offers')) {
           const mockOffers = [
             { 
               id: 'offer1', 
               title: 'Advance Booking Bonus', 
               description: 'Pay ₹100 advance & get 5% off your total bill!', 
               imageName: 'salon-hero.jpg', // Placeholder, update with actual offer images later
               linkUrl: '/book-appointment' // Example link
             },
             { 
               id: 'offer2', 
               title: 'Mid-Week Special', 
               description: '15% off all hair color services. (Wednesdays only)', 
               imageName: 'haircut.jpg', // Placeholder
             },
             { 
               id: 'offer3', 
               title: 'Student Discount', 
               description: 'Show your valid student ID and get 10% off any service.', 
               imageName: 'skincare.jpg', // Placeholder
             },
             { 
               id: 'offer4', 
               title: 'Loyalty Treat', 
               description: 'Get a free hair spa on your 5th visit!', 
               imageName: 'bridal.jpg', // Placeholder
             }
           ];
           resolve({ data: { data: mockOffers } } as R);
         } else if (url.includes('/customers/search')) {
             const mockCustomers = [
               { id: 'cust1', firstName: 'Priya', lastName: 'Sharma', email: 'priya.sharma@example.com', phoneNumber: '9876543210', totalVisit: 5, status: 'active' },
               { id: 'cust2', firstName: 'Amit', lastName: 'Patel', email: 'amit.patel@example.com', phoneNumber: '9876543211', totalVisit: 12, status: 'active' },
               { id: 'cust3', firstName: 'Sunita', lastName: 'Gupta', email: 'sunita.gupta@example.com', phoneNumber: '9876543212', totalVisit: 2, status: 'inactive' },
             ];
             const query = config?.params?.q?.toLowerCase() || '';
             const results = mockCustomers.filter(c => 
               c.firstName?.toLowerCase().includes(query) ||
               c.lastName?.toLowerCase().includes(query) ||
               c.phoneNumber.includes(query) ||
               c.email?.toLowerCase().includes(query)
             );
             resolve({ data: results } as R);
         } else if (url.includes('/customers')) {
           const mockCustomers = [
             { id: 'cust1', firstName: 'Priya', lastName: 'Sharma', email: 'priya.sharma@example.com', phoneNumber: '9876543210', totalVisit: 5, status: 'active' },
             { id: 'cust2', firstName: 'Amit', lastName: 'Patel', email: 'amit.patel@example.com', phoneNumber: '9876543211', totalVisit: 12, status: 'active' },
             { id: 'cust3', firstName: 'Sunita', lastName: 'Gupta', email: 'sunita.gupta@example.com', phoneNumber: '9876543212', totalVisit: 2, status: 'inactive' },
           ];
           const page = config?.params?.page || 1;
           const limit = config?.params?.limit || 10;
           const offset = (page - 1) * limit;
           const paginatedCustomers = mockCustomers.slice(offset, offset + limit);
 
          resolve({ data: {
             totalItems: mockCustomers.length,
             totalPages: Math.ceil(mockCustomers.length / limit),
             currentPage: page,
             customers: paginatedCustomers,
           } } as R);
         }
         else {
           reject({ response: { data: { message: `Mock endpoint for GET ${url} not found` }, status: 404 }, isAxiosError: true, toJSON: () => ({}) });
         }
       }, MOCK_DELAY);
     });
   },
   // Add put, delete, etc. as needed
 };
 
const apiService = MOCK_API ? mockApiService : realApiService;
 
export default apiService as AxiosInstance & typeof mockApiService; // Cast to satisfy both real and mock structures
 